import { useQuery } from '@tanstack/react-query';
import { useApp } from './use-app';

interface Props {
  recordId: string
  options?: {
    excludeLinkedTo?: boolean
    excludeLinkedFrom?: boolean
  }
}

/*
  This hook returns a LinkedRecordResponse. This requires
  two sets of SQL calls. The first gets the raw "record_link"
  data. Next, it gets a subset ("summary") of Record data.
  The summaries are used to construct the LinkedRecord objects.

  It may be helpful to understand "linkedFrom" vs "linkedTo".
  Everything is from the viewpoint of the Record represented
  by the "recordId" prop. So, when other Records reference
  "recordId", our Record is "linkedFrom" those other Records.
  When our Record has fields that reference other Records,
  our Record is "linkedTo" the other Records.

  To find "linkedFrom" Records, we look for "recordId" in the
  "linked_record_id" column of the `record_links` table. To find
  "linkedTo" Records, we look for "recordId" in the "record_id"
  column of the table.
*/
export interface LinkedRecordResponse {
  linkedTo: LinkedRecordData[]
  linkedFrom: LinkedRecordData[]
}

export interface LinkedRecordData {
  formName: string
  formId: string
  recordId: string
  recordTitle: string
  recordKey?: string
  linkedFieldKey: string
}

interface RawLinkedRecordRows {
  objects: RawLinkedRecordRow[]
}
interface RawLinkedRecordRow {
  formname: string
  hasrecordkey: number
  key: string
  formid: string
  recordid: string
  linkedformid: string
  linkedrecordid: string
}

interface SummaryRecordRows {
  objects: SummaryRecordRow[]
}
interface SummaryRecordRow {
  formname: string
  formid: string
  title: string
  recordkey: string
  recordid: string
}

type LinkedRecordDataResult = PromiseSettledResult<RawLinkedRecordRows> | EmptyResponse;
type SummaryRecordRowResult = PromiseSettledResult<SummaryRecordRows>;
type EmptyResponse = { status: string }

/* LinkedRecordDataByForm organizes all the links by Form
 { 'form-1-resource-id': { 'akey': ['record-1-resource-id', 'record-2-resource-id'] } }
*/
interface LinkedRecordDataByForm {
  [key: string]: { [key: string]: string[] }
}

interface LinkedTargets {
  linkedTo: string[]
  linkedFrom: string[]
  byForms: LinkedRecordDataByForm
  formsWithRecordKeys: string[]
}

const rawLinkedRecordQuery = (recordId: string, linkedFrom: boolean): string => {
  const target = linkedFrom ? '' : 'linked_';
  const source = linkedFrom ? 'linked_' : '';

  return `
    select
      c.table_name as formName,
      (case when c.table_name is null then 0 else 1 end) as hasRecordKey,
      rl.key,
      rl.form_id as formId,
      rl.record_id as recordId,
      rl.linked_form_id as linkedFormId,
      rl.linked_record_id as linkedRecordId
    from record_links rl
    inner join columns c
      on rl.${source}form_id = c.form_id
      and c.name = '_record_key'
    where rl.${target}record_id = '${recordId}';
  `;
};

const summaryRecordQuery = (
  formId: string,
  recordIds: string[],
  hasRecordKey: boolean,
): string => {
  const ids = recordIds.map((id) => (`'${id}'`)).join(',');
  const recordKeyField = hasRecordKey ? 'r._record_key' : "''";

  return `
    select
      t.name as formName,
      t.form_id as formId,
      r._title as title,
      ${recordKeyField} as recordKey,
      r._record_id as recordId
    from "${formId}/_full" r
    inner join tables t
    on t.type = 'form' and t.form_id = '${formId}'
    where r._record_id in (${ids});
  `;
};

const useLinkedRecords = (props: Props) => {
  const { recordId, options } = props;
  const app = useApp();
  const apiClient = app.api.query;

  /* eslint-disable no-param-reassign */
  const addRecordToByFormsObject = (
    byForms: LinkedRecordDataByForm, targetFormId: string, key: string, targetRecordId: string,
  ) => {
    if (!byForms[targetFormId]) {
      byForms[targetFormId] = {};
    }
    if (!byForms[targetFormId][key]) {
      byForms[targetFormId][key] = [];
    }
    byForms[targetFormId][key].push(targetRecordId);
  };
  /* eslint-disable no-param-reassign */

  const constructLinkedTargets = (
    linkedTo: LinkedRecordDataResult,
    linkedFrom: LinkedRecordDataResult,
  ): LinkedTargets => {
    const linkedTargets: LinkedTargets = {
      linkedTo: [], linkedFrom: [], byForms: {}, formsWithRecordKeys: [],
    };

    if (linkedTo.status === 'fulfilled') {
      const { value } = linkedTo as PromiseFulfilledResult<RawLinkedRecordRows>;
      value.objects.forEach((row: RawLinkedRecordRow) => {
        linkedTargets.linkedTo.push(row.linkedrecordid);
        addRecordToByFormsObject(
          linkedTargets.byForms, row.linkedformid, row.key, row.linkedrecordid,
        );
        if (row.hasrecordkey) {
          linkedTargets.formsWithRecordKeys.push(row.formid);
        }
      });
    }

    if (linkedFrom.status === 'fulfilled') {
      const { value } = linkedFrom as PromiseFulfilledResult<RawLinkedRecordRows>;
      value.objects.forEach((row: RawLinkedRecordRow) => {
        linkedTargets.linkedFrom.push(row.recordid);
        addRecordToByFormsObject(linkedTargets.byForms, row.formid, row.key, row.recordid);
        if (row.hasrecordkey) {
          linkedTargets.formsWithRecordKeys.push(row.formid);
        }
      });
    }

    return linkedTargets;
  };

  const translateSummaryRecordRow = (
    row: SummaryRecordRow, linkedFieldKey: string,
  ): LinkedRecordData => (
    {
      formName: row.formname,
      formId: row.formid,
      recordId: row.recordid,
      recordTitle: row.title,
      recordKey: row.recordkey,
      linkedFieldKey,
    }
  );

  const runQueries = async (queries: string[]) => (
    Promise.allSettled(queries.map((q) => apiClient.run({ q })))
  );

  const buildSummaryRecordQueries = (linkedTargets: LinkedTargets) => (
    Object.keys(linkedTargets.byForms).map((formId) => {
      const formData = linkedTargets.byForms[formId];
      const hasRecordKey = linkedTargets.formsWithRecordKeys.includes(formId);
      let recordIds: string[] = [];
      Object.keys(formData).forEach((key) => {
        recordIds = recordIds.concat(formData[key]);
      });
      const q = summaryRecordQuery(formId, recordIds, hasRecordKey);
      return q;
    })
  );

  const extractLinkedRecordData = (
    targets: string[], row: SummaryRecordRow, byForms: LinkedRecordDataByForm,
  ): LinkedRecordData[] => {
    const { recordid, formid } = row;
    const linkedRecordDatas: LinkedRecordData[] = [];
    if (!targets.includes(recordid)) {
      return linkedRecordDatas;
    }

    const byFormsKeys = byForms[formid];
    Object.keys(byFormsKeys).forEach((key) => {
      if (byFormsKeys[key].includes(recordid)) {
        const data = translateSummaryRecordRow(row, key);
        linkedRecordDatas.push(data);
      }
    });

    return linkedRecordDatas;
  };

  const processSummaryQueries = (
    linkedTargets: LinkedTargets, summaryResults: SummaryRecordRowResult[],
  ): LinkedRecordResponse => {
    const linkedRecordResponse: LinkedRecordResponse = { linkedTo: [], linkedFrom: [] };
    const { linkedTo, linkedFrom, byForms } = linkedTargets;

    summaryResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        const { value } = result;
        value.objects.forEach((row: SummaryRecordRow) => {
          const linkedTos = extractLinkedRecordData(linkedTo, row, byForms);
          const linkedFroms = extractLinkedRecordData(linkedFrom, row, byForms);

          linkedTos.forEach((linkedItem) => (
            linkedRecordResponse.linkedTo.push(linkedItem)
          ));
          linkedFroms.forEach((linkedItem) => (
            linkedRecordResponse.linkedFrom.push(linkedItem)
          ));
        });
      } else {
        // We couldn't get info about the table, probably because we don't have permission
        const { reason: { body } } = result;
        const message = body.toString();
        Object.keys(byForms).forEach((formId) => {
          if (message.includes(formId)) {
            // We don't have access to this form, but we still want to give something back
            const formFields = byForms[formId];
            Object.keys(formFields).forEach((fieldKey) => {
              const recIds = formFields[fieldKey];
              recIds.forEach((recId) => {
                const linkedRecord: LinkedRecordData = {
                  formName: '',
                  formId,
                  recordId: recId,
                  recordTitle: '',
                  recordKey: '',
                  linkedFieldKey: fieldKey,
                };

                if (linkedTo.includes(recId)) {
                  linkedRecordResponse.linkedTo.push(linkedRecord);
                }

                if (linkedFrom.includes(recId)) {
                  linkedRecordResponse.linkedFrom.push(linkedRecord);
                }
              });
            });
          }
        });
      }
    });

    return linkedRecordResponse;
  };

  const fetchLinkedRecords = async (): Promise<LinkedRecordResponse> => {
    const withLinkedTo = !options?.excludeLinkedTo;
    const withLinkedFrom = !options?.excludeLinkedFrom;

    const rawLinkQueries = [];
    if (withLinkedTo) {
      // @ts-ignore
      rawLinkQueries.push(rawLinkedRecordQuery(recordId, true));
    }
    if (withLinkedFrom) {
      // @ts-ignore
      rawLinkQueries.push(rawLinkedRecordQuery(recordId, false));
    }
    const emptyResponse: EmptyResponse = { status: 'empty' };
    const rawLinkDataResults: LinkedRecordDataResult[] = await runQueries(rawLinkQueries);
    const rawLinkedToResults = withLinkedTo ? rawLinkDataResults[0] : emptyResponse;
    const rawLinkedFromResults = withLinkedFrom
      ? rawLinkDataResults[rawLinkDataResults.length - 1]
      : emptyResponse;

    const linkedTargets = constructLinkedTargets(rawLinkedToResults, rawLinkedFromResults);
    const summaryQueries = buildSummaryRecordQueries(linkedTargets);

    const summaryResults: SummaryRecordRowResult[] = await runQueries(summaryQueries);
    return processSummaryQueries(linkedTargets, summaryResults);
  };

  return useQuery(['linked_records', recordId, options], fetchLinkedRecords,
    {
      refetchOnWindowFocus: false,
    });
};

export default useLinkedRecords;
